<template>
    <div class="bg-white rounded-lg shadow pb-4">
        <!-- Header -->
        <div class="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 class="text-lg sm:text-xl font-bold text-gray-900" style="font-family: 'Red Hat Text', sans-serif;">
                        {{ foundation.name }} - Campaign Leaderboard
                    </h2>
                    <p class="text-sm text-gray-600 mt-1">
                        All campaigns ranked by Impact Score
                    </p>
                </div>
                <div class="flex items-center gap-2">
                    <button
                        @click="goToMyCampaignPage(); $nextTick(() => scrollToMyCampaign())"
                        class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 text-sm"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Find Me
                    </button>
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search campaigns..."
                        class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                </div>
            </div>
        </div>

        <!-- Sort Tabs (Table Header Style) -->
        <div class="mt-6 mx-4 sm:mx-6 bg-white rounded-t-lg shadow-sm border-t border-l border-r border-pink-100 overflow-hidden">
            <div class="flex border-b-2 border-pink-200">
                <button @click="sortBy = 'rank'"
                        :class="sortBy === 'rank' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'rank' ? 'true' : null"
                        class="flex-1 px-2 py-2 sm:px-6 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-normal sm:tracking-wider border-r border-gray-200 transition-colors">
                    Overall
                </button>

                <button @click="sortBy = 'donors-desc'"
                        :class="sortBy === 'donors-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'donors-desc' ? 'true' : null"
                        class="flex-1 px-2 py-2 sm:px-6 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-normal sm:tracking-wider border-r border-gray-200 transition-colors">
                    Donors
                </button>

                <button @click="sortBy = 'raised-desc'"
                        :class="sortBy === 'raised-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'raised-desc' ? 'true' : null"
                        class="flex-1 px-2 py-2 sm:px-6 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-normal sm:tracking-wider border-r border-gray-200 transition-colors">
                    Raised
                </button>

                <button @click="sortBy = 'views-desc'"
                        :class="sortBy === 'views-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'views-desc' ? 'true' : null"
                        class="flex-1 px-2 py-2 sm:px-6 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-normal sm:tracking-wider border-r border-gray-200 transition-colors">
                    Views
                </button>

                <button @click="sortBy = 'conversion-desc'"
                        :class="sortBy === 'conversion-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'conversion-desc' ? 'true' : null"
                        class="flex-1 px-2 py-2 sm:px-6 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-normal sm:tracking-wider transition-colors">
                    Conversion
                </button>
            </div>
        </div>

        <!-- Leaderboard Table (Desktop) -->
        <div class="hidden md:block mx-4 sm:mx-6 mb-8 bg-white rounded-b-lg shadow-sm border-b border-l border-r border-pink-100 overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                            Rank
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Organization / Campaign
                        </th>
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Score
                        </th>
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Donors
                        </th>
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Raised
                        </th>
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Views
                        </th>
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Conversion
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="campaign in paginatedLeaderboard" :key="campaign.id"
                        :ref="campaign.is_current ? 'currentCampaignRow' : undefined"
                        :class="campaign.is_current ? 'bg-blue-100 border-l-4 border-blue-600 shadow-md' : 'hover:bg-gray-50'">
                        <!-- Rank -->
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center gap-2">
                                <span v-if="campaign.rank === 1" class="text-2xl">🥇</span>
                                <span v-else-if="campaign.rank === 2" class="text-2xl">🥈</span>
                                <span v-else-if="campaign.rank === 3" class="text-2xl">🥉</span>
                                <span v-else class="text-sm font-semibold text-gray-600">#{{ campaign.rank }}</span>
                                <span v-if="campaign.is_current" class="ml-1 text-blue-600 font-bold">👈</span>
                            </div>
                        </td>

                        <!-- Campaign Name -->
                        <td class="px-6 py-4">
                            <div class="flex items-center">
                                <div>
                                    <div class="text-sm font-medium text-gray-900">
                                        {{ campaign.business_name || campaign.name }}
                                        <span v-if="campaign.is_current" class="ml-2 px-2 py-1 text-xs font-semibold bg-blue-200 text-blue-800 rounded-full">
                                            You
                                        </span>
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ campaign.is_foundation ? 'Foundation' : 'Supporting Campaign' }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- Score -->
                        <td :class="{'active-cell': highlightedColumns.includes('score'), 'muted-cell': !highlightedColumns.includes('score')}" class="px-6 py-4 whitespace-nowrap text-center">
                            <div class="text-lg font-bold">{{ formatScore(campaign.score) }}</div>
                        </td>

                        <!-- Donors -->
                        <td :class="{'active-cell': highlightedColumns.includes('donors'), 'muted-cell': !highlightedColumns.includes('donors')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {{ formatNumber(campaign.donor_count) }}
                        </td>

                        <!-- Total Raised -->
                        <td :class="{'active-cell': highlightedColumns.includes('raised'), 'muted-cell': !highlightedColumns.includes('raised')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">
                            ${{ formatCurrency(campaign.total_raised) }}
                        </td>

                        <!-- Views -->
                        <td :class="{'active-cell': highlightedColumns.includes('views'), 'muted-cell': !highlightedColumns.includes('views')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {{ formatNumber(campaign.views) }}
                        </td>

                        <!-- Conversion -->
                        <td :class="{'active-cell': highlightedColumns.includes('conversion'), 'muted-cell': !highlightedColumns.includes('conversion')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {{ getDisplayConversion(campaign) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- No Results -->
            <div v-if="sortedAndFilteredLeaderboard.length === 0" class="px-6 py-12 text-center text-gray-500">
                No campaigns found matching "{{ searchQuery }}"
            </div>
        </div>

        <!-- Leaderboard Cards (Mobile) -->
        <div class="md:hidden mx-4 sm:mx-6 mb-8 bg-white rounded-b-lg shadow-sm border-b border-l border-r border-pink-100 overflow-hidden divide-y divide-gray-200">
            <div v-for="campaign in paginatedLeaderboard" :key="campaign.id"
                 :ref="campaign.is_current ? 'currentCampaignRow' : undefined"
                 :class="campaign.is_current ? 'bg-blue-100 border-l-4 border-blue-600 shadow-md' : ''"
                 class="px-4 py-4">
                <div class="flex items-start justify-between mb-2">
                    <!-- Rank & Name -->
                    <div class="flex items-start flex-1">
                        <div class="mr-3 mt-1">
                            <div class="flex items-center gap-1">
                                <span v-if="campaign.rank === 1" class="text-2xl">🥇</span>
                                <span v-else-if="campaign.rank === 2" class="text-2xl">🥈</span>
                                <span v-else-if="campaign.rank === 3" class="text-2xl">🥉</span>
                                <span v-else class="font-semibold text-gray-600">#{{ campaign.rank }}</span>
                                <span v-if="campaign.is_current" class="text-blue-600 font-bold text-lg">👈</span>
                            </div>
                        </div>
                        <div class="flex-1">
                            <h3 class="text-sm font-medium text-gray-900">
                                {{ campaign.business_name || campaign.name }}
                                <span v-if="campaign.is_current" class="ml-2 px-2 py-1 text-xs font-semibold bg-blue-200 text-blue-800 rounded-full">
                                    You
                                </span>
                            </h3>
                            <p class="text-xs text-gray-500">
                                {{ campaign.is_foundation ? 'Foundation' : 'Supporting Campaign' }}
                            </p>
                        </div>
                    </div>
                    <!-- Score -->
                    <div :class="{'active-cell': highlightedColumns.includes('score'), 'muted-cell': !highlightedColumns.includes('score')}" class="text-right ml-3">
                        <div class="text-xl font-bold">{{ formatScore(campaign.score) }}</div>
                    </div>
                </div>
                <!-- Stats -->
                <div class="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div class="bg-gray-50 rounded p-2">
                        <div class="text-gray-600">Donors</div>
                        <div :class="{'active-cell': highlightedColumns.includes('donors'), 'muted-cell': !highlightedColumns.includes('donors')}" class="font-semibold">{{ formatNumber(campaign.donor_count) }}</div>
                    </div>
                    <div class="bg-gray-50 rounded p-2">
                        <div class="text-gray-600">Raised</div>
                        <div :class="{'active-cell': highlightedColumns.includes('raised'), 'muted-cell': !highlightedColumns.includes('raised')}" class="font-semibold">${{ formatCurrency(campaign.total_raised) }}</div>
                    </div>
                    <div class="bg-gray-50 rounded p-2">
                        <div class="text-gray-600">Views</div>
                        <div :class="{'active-cell': highlightedColumns.includes('views'), 'muted-cell': !highlightedColumns.includes('views')}" class="font-semibold">{{ formatNumber(campaign.views) }}</div>
                    </div>
                    <div class="bg-gray-50 rounded p-2">
                        <div class="text-gray-600">Conversion</div>
                        <div :class="{'active-cell': highlightedColumns.includes('conversion'), 'muted-cell': !highlightedColumns.includes('conversion')}" class="font-semibold">{{ getDisplayConversion(campaign) }}</div>
                    </div>
                </div>
            </div>

            <!-- No Results -->
            <div v-if="sortedAndFilteredLeaderboard.length === 0" class="px-4 py-12 text-center text-gray-500 text-sm">
                No campaigns found matching "{{ searchQuery }}"
            </div>
        </div>

        <!-- Pagination Controls -->
        <div v-if="showPagination" class="mx-4 sm:mx-6 mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-pink-100 p-4">
            <!-- Results Info -->
            <div class="text-sm text-gray-600">
                Showing {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, sortedAndFilteredLeaderboard.length) }}
                of {{ sortedAndFilteredLeaderboard.length }} campaigns
            </div>

            <!-- Page Controls -->
            <div class="flex items-center gap-2">
                <button
                    @click="goToPage(1)"
                    :disabled="currentPage === 1"
                    :class="currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-100'"
                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                >
                    First
                </button>
                <button
                    @click="prevPage"
                    :disabled="currentPage === 1"
                    :class="currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-100'"
                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                >
                    ← Prev
                </button>

                <!-- Page Numbers -->
                <div class="flex items-center gap-1">
                    <template v-for="page in totalPages" :key="page">
                        <button
                            v-if="page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)"
                            @click="goToPage(page)"
                            :class="page === currentPage
                                ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-pink-100 border border-gray-300'"
                            class="px-3 py-2 text-sm font-medium rounded-lg transition-colors min-w-[40px]"
                        >
                            {{ page }}
                        </button>
                        <span v-else-if="page === currentPage - 2 || page === currentPage + 2" class="px-1 text-gray-400">...</span>
                    </template>
                </div>

                <button
                    @click="nextPage"
                    :disabled="currentPage === totalPages"
                    :class="currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-100'"
                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                >
                    Next →
                </button>
                <button
                    @click="goToPage(totalPages)"
                    :disabled="currentPage === totalPages"
                    :class="currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-100'"
                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                >
                    Last
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LeaderboardTab',

    props: {
        leaderboardData: {
            type: Array,
            required: true
        },
        foundation: {
            type: Object,
            required: true
        },
        currentCampaignId: {
            type: [Number, String],
            required: true
        }
    },

    data() {
        return {
            searchQuery: '',
            sortBy: 'rank',
            currentPage: 1,
            perPage: 25,
        }
    },

    computed: {
        filteredLeaderboard() {
            if (!this.searchQuery) {
                return this.leaderboardData
            }

            const query = this.searchQuery.toLowerCase()
            return this.leaderboardData.filter(campaign =>
                campaign.name.toLowerCase().includes(query) ||
                (campaign.business_name && campaign.business_name.toLowerCase().includes(query))
            )
        },

        sortedAndFilteredLeaderboard() {
            const filtered = this.filteredLeaderboard
            return this.sortCampaigns(filtered)
        },

        // Paginated subset for rendering
        paginatedLeaderboard() {
            const start = (this.currentPage - 1) * this.perPage
            const end = start + this.perPage
            return this.sortedAndFilteredLeaderboard.slice(start, end)
        },

        totalPages() {
            return Math.ceil(this.sortedAndFilteredLeaderboard.length / this.perPage)
        },

        // Show pagination if more than one page
        showPagination() {
            return this.totalPages > 1
        },

        highlightedColumns() {
            // Returns array of column identifiers to highlight based on active sort
            switch(this.sortBy) {
                case 'rank':
                    return ['score']  // Only highlight score, not rank
                case 'donors-desc':
                    return ['donors']
                case 'raised-desc':
                    return ['raised']
                case 'conversion-desc':
                    return ['conversion']
                case 'views-desc':
                    return ['views']
                default:
                    return []
            }
        }
    },

    methods: {
        formatNumber(num) {
            return Number(num).toLocaleString()
        },
        formatCurrency(cents) {
            // Convert cents to dollars
            const dollars = (cents || 0) / 100
            return dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        },
        formatScore(score) {
            return Number(score).toFixed(1)
        },
        isValidConversion(campaign) {
            // Require at least 10 views for valid conversion rate
            return (campaign.views || 0) >= 10
        },
        getDisplayConversion(campaign) {
            if (!this.isValidConversion(campaign)) {
                return 'N/A'
            }
            const conversionRate = Number(campaign.conversion)
            return conversionRate ? conversionRate.toFixed(2) + '%' : 'N/A'
        },
        sortCampaigns(campaigns) {
            const sorted = [...campaigns]

            switch(this.sortBy) {
                case 'rank':
                    return sorted.sort((a, b) => a.rank - b.rank)
                case 'donors-desc':
                    return sorted.sort((a, b) => (b.donor_count || 0) - (a.donor_count || 0))
                case 'raised-desc':
                    return sorted.sort((a, b) => (b.total_raised || 0) - (a.total_raised || 0))
                case 'conversion-desc':
                    return sorted.sort((a, b) => {
                        const aRate = this.isValidConversion(a) ? (Number(a.conversion) || 0) : 0
                        const bRate = this.isValidConversion(b) ? (Number(b.conversion) || 0) : 0
                        return bRate - aRate
                    })
                case 'views-desc':
                    return sorted.sort((a, b) => (b.views || 0) - (a.views || 0))
                default:
                    return sorted
            }
        },
        scrollToMyCampaign() {
            // Find the current campaign in the data
            const currentCampaign = this.sortedAndFilteredLeaderboard.find(c => c.is_current)

            if (!currentCampaign) {
                console.warn('No current campaign found in leaderboard')
                return
            }

            // Access the ref (Vue 3 refs with v-for return an array)
            const refElements = this.$refs.currentCampaignRow

            if (!refElements) {
                console.warn('Ref not found for current campaign row')
                return
            }

            // Get the first element (should only be one current campaign)
            const targetElement = Array.isArray(refElements) ? refElements[0] : refElements

            if (targetElement) {
                // Smooth scroll to the element (nearest = minimal disruption)
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                })
            } else {
                console.warn('Target element is null or undefined')
            }
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--
            }
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++
            }
        },
        // Find which page has the current campaign and go there
        goToMyCampaignPage() {
            const index = this.sortedAndFilteredLeaderboard.findIndex(c => c.is_current)
            if (index >= 0) {
                this.currentPage = Math.floor(index / this.perPage) + 1
            }
        }
    },

    watch: {
        // Reset to page 1 when search or sort changes
        searchQuery() {
            this.currentPage = 1
        },
        sortBy() {
            this.currentPage = 1
        }
    }
}
</script>

<style scoped>
/* Ensure proper spacing when scrolled into view */
tr.bg-blue-100,
div.bg-blue-100 {
    scroll-margin-top: 100px;
    scroll-margin-bottom: 100px;
}

/* Active column data - pink, larger, bolder (NO background) */
.active-cell {
    font-weight: 700 !important;
    font-size: 1.15em !important;
    color: #db2777 !important; /* pink-600 */
    transition: all 0.2s ease;
}

/* Muted column data - gray, reduced opacity (NO background) */
.muted-cell {
    color: #9ca3af !important; /* gray-400 */
    opacity: 0.65;
    font-weight: 400;
    transition: all 0.2s ease;
}

.muted-cell:hover {
    opacity: 0.85;
}
</style>
